<template>
  <div class="relative w-[90vw] max-w-[1300px] mx-auto mt-auto mb-auto">
    <!-- Header with elegant styling -->
    <div class="flex justify-center mb-4">
      <h2 class="text-4xl md:text-6xl font-bold text-base-content">
        The <span class="text-secondary font-bold text-shadow-lg">STE</span> Advantage
      </h2>
    </div>
    
    <!-- Introduction text -->
    <div class="text-center mb-8">
      <p class="text-base md:text-xl text-white mb-4 flex items-center justify-center flex-wrap gap-2">
        Discover 
        <button 
          @click="navigateToStory" 
          class="inline-flex items-center gap-1 px-4 py-1 bg-gradient-to-r from-primary/90 to-primary/70 rounded-full text-white font-semibold
                 hover:shadow-lg hover:shadow-primary/20 hover:scale-105 transition-all cursor-pointer duration-300
                 border border-primary/30 mx-1"
        >
          <span>Our Story</span>
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-xs" />
        </button>
        and the core values that drive our success.
      </p>
    </div>

    <!-- Modern carousel implementation -->
    <div class="carousel-container relative" ref="carouselContainer">

      <!-- Card carousel -->
      <div 
        class="flex overflow-x-hidden py-12 px-4 md:px-8 gap-4 md:gap-6 items-center justify-start" 
        ref="carousel"
      >
        <div 
          v-for="(item, index) in items" 
          :key="index"
          class="value-card flex-shrink-0 object-fit w-[280px] md:w-[520px] h-100 rounded-xl relative overflow-hidden group transition-all duration-500 ease-out"
          :class="{ 'is-selected': currentIndex === index }"
          @click="navigateTo(index)"
        >
          <!-- Background image with gradient overlay -->
          <div class="absolute inset-0 overflow-hidden">
            <img 
              :src="item.image" 
              :alt="item.title"
              class="w-full h-full object-contain transition-all duration-700 ease-out transform group-hover:scale-110"
              :class="{'grayscale brightness-[0.7]': currentIndex === index, 'brightness-[0.4]': currentIndex !== index}"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-base-300/90 via-transparent to-transparent"></div>
          </div>

          <!-- Card content with animations -->
          <div class="absolute inset-0 flex flex-col justify-between p-6 transition-all duration-500">
            <!-- Title area with animation -->
            <div 
              class="value-title-container backdrop-blur-sm bg-base-300/70 rounded-lg border border-white/20 p-4 transform transition-all duration-500"
              :class="{'translate-y-0': currentIndex === index, 'translate-y-52': currentIndex !== index}"
            >
              <h3 class="text-xl md:text-2xl font-bold text-white text-center">
                {{ item.title }} 
                <span v-if="item.subtitle" class="block mt-1">{{ item.subtitle }}</span>
              </h3>
            </div>

            <!-- Description area with animation -->
            <div 
              class="description-container rounded-2xl backdrop-blur-md 
                bg-gradient-to-br from-primary/90 to-primary/40 
                border border-white/10 shadow-xl p-4 pb-0 transform transition-all duration-700"
              :class="{'translate-y-0 opacity-100': currentIndex === index, 'translate-y-full opacity-0': currentIndex !== index}"
            >
            <!-- Card background effects -->
              <div class="absolute inset-0 rounded-2xl bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.1),_transparent_70%)] z-0"></div>
              <div 
                class="absolute inset-0 bg-gradient-to-tr from-secondary/5 via-transparent to-secondary/10 
                      opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-700 z-0"
              ></div>
              <p class="text-base-content text-base md:text-smaller text-left">
                {{ item.description }}
                <span 
                  @click.stop="navigateToValue(item.title)"
                  class="inline-flex items-center flex-row justify-end gap-1 text-secondary w-full hover:text-secondary/80 ml-1 cursor-pointer transition-colors duration-300"
                  href="javascript:void(0);"
                >
                  <font-awesome-icon 
                    :icon="['fas', 'arrow-right']" 
                    class="text-xl transition-transform duration-300 group-hover:translate-x-1" 
                  />
              </span>
              </p>
            </div>
          </div>

          <!-- Interactive hover effect elements -->
          <div 
            class="absolute inset-0 pointer-events-none transition-opacity duration-500"
            :class="{'opacity-0': currentIndex === index, 'opacity-100': currentIndex !== index}"
          >
            <div class="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-base-content/60 to-transparent"></div>
          </div>
        </div>
      </div>

      <!-- Indicator dots -->
      <div class="flex justify-center mt-2 gap-2 pb-2">
        <button 
          v-for="(_, index) in items" 
          :key="'dot-'+index"
          @click="navigateTo(index)"
          class="w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300"
          :class="currentIndex === index ? 'bg-primary scale-125' : 'bg-white/40 hover:bg-white/70'"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ValueSlider',
  data() {
    return {
      items: [
        {
          title: 'Respect',
          subtitle: '', 
          description: 'We treat every associate and customer with honesty and integrity, tapping into each person’s potential to ensure long-term success for everyone.',
          image: '/img/values/respect.png',
        },
        {
          title: 'Opportunity',
          subtitle: '', 
          description: 'We believe that every associate deserves a fair chance to pursue his or her dream, and that we are all obliged to create opportunities that allow for the growth and success of others.',
          image: '/img/values/opportunity.png',
        },
        {
          title: 'Work Hard / Have Fun',
          subtitle: '', 
          description: 'We respect the passion shown by associates who push themselves to find their full potential, and we appreciate the positive energy that our people bring to the workplace.',
          image: '/img/values/workhard.png',
        },
        {
          title: 'Entrepreneurial Spirit',
          subtitle: '', 
          description: 'We continuously improve our business practices by encouraging innovation, imagination, critical thinking, and determination from associates at every level.',
          image: '/img/values/entrepreneurialspirit.png',
        },
        {
          title: 'Family',
          subtitle: '', 
          description: 'We care for our associates, their families, and our extended family of customers beyond organizational relationships; we rely on and trust in one another for continued support and collaboration.',
          image: '/img/values/family.png',
        },
        {
          title: 'Give Back',
          subtitle: '', 
          description: 'We express gratitude for what we have by donating our time and resources; we share what we have to help improve our communities and our world.',
          image: '/img/values/giveback.png',
        },
        {
          title: 'American Pride',
          subtitle: '', 
          description: 'We pay tribute to our country, believe in the American Dream and all it stands for, and honor and respect the men and women who serve our great nation.',
          image: '/img/values/americanpride.png',
        },
        // Additional items go here
      ],
      currentIndex: 0,
      isTransitioning: false,
      touchStartX: 0,
      touchEndX: 0,
    };
  },
  mounted() {
    this.setupTouchEvents();
    this.setupKeyboardEvents();
    this.setupVisibilityChange();
    window.addEventListener('resize', this.handleResize);
    this.centerCurrentCard();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.cleanupEventListeners();
  },
  methods: {
    setupTouchEvents() {
      const carousel = this.$refs.carousel;
      if (!carousel) return;
      
      carousel.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      carousel.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    },
    
    setupKeyboardEvents() {
      window.addEventListener('keydown', this.handleKeydown);
    },
    
    setupVisibilityChange() {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    
    cleanupEventListeners() {
      const carousel = this.$refs.carousel;
      if (carousel) {
        carousel.removeEventListener('touchstart', this.handleTouchStart);
        carousel.removeEventListener('touchend', this.handleTouchEnd);
      }
      
      window.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    },
    
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    },
    
    handleSwipe() {
      const swipeThreshold = 50;
      if (this.touchEndX < this.touchStartX - swipeThreshold) {
        this.nextSlide();
      }
      if (this.touchEndX > this.touchStartX + swipeThreshold) {
        this.prevSlide();
      }
    },
    
    handleKeydown(e) {
      if (e.key === 'ArrowRight') this.nextSlide();
      if (e.key === 'ArrowLeft') this.prevSlide();
    },
    
    handleVisibilityChange() {
      // Pause any animations when tab is not visible
      if (document.hidden) {
        // Could pause any autoplay here
      } else {
        // Resume animations when visible again
      }
    },
    
    navigateTo(index) {
      if (this.isTransitioning || this.currentIndex === index) return;
      
      this.isTransitioning = true;
      this.currentIndex = index;
      this.centerCurrentCard();
      
      setTimeout(() => {
        this.isTransitioning = false;
      }, 500);
    },
    
    nextSlide() {
      if (this.isTransitioning) return;
      const newIndex = (this.currentIndex + 1) % this.items.length;
      this.navigateTo(newIndex);
    },
    
    prevSlide() {
      if (this.isTransitioning) return;
      const newIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
      this.navigateTo(newIndex);
    },
    
    centerCurrentCard() {
      this.$nextTick(() => {
        const carousel = this.$refs.carousel;
        const cards = carousel.querySelectorAll('.value-card');
        const selectedCard = cards[this.currentIndex];
        
        if (selectedCard) {
          const containerWidth = carousel.offsetWidth;
          const cardWidth = selectedCard.offsetWidth;
          const cardLeft = selectedCard.offsetLeft;
          
          // Calculate the scroll position to center the card
          const scrollPosition = cardLeft - (containerWidth / 2) + (cardWidth / 2);
          
          // Smooth scroll to the position
          carousel.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      });
    },
    
    handleResize() {
      this.centerCurrentCard();
    },

    /**
     * Navigate to the AboutUsPage and scroll to the specific value section
     * @param {string} valueTitle - The title of the value to navigate to
     */
    navigateToValue(valueTitle) {
      // Prevent event propagation to avoid triggering the card's click handler
      event.stopPropagation();
      
      // Convert the value title to a kebab-case format for the URL hash
      const kebabCaseValue = valueTitle
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/\//g, '-'); // Handle values with slashes like "Work Hard / Have Fun"
      
      // Navigate to the AboutUsPage with the value hash
      this.$router.push({
        path: '/about-us',
        hash: `#value-${kebabCaseValue}`
      });
    },

    /**
     * Navigate to the AboutUsPage and scroll to the OurStory section
     */
    navigateToStory() {
      // Navigate to the AboutUsPage with the OurStory section hash
      this.$router.push({
        path: '/about-us',
        hash: '#slide2'
      });
    }
  }
};
</script>

<style scoped>
.carousel-container {
  mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
}

.value-card {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  transform: scale(0.9);
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.value-card.is-selected {
  transform: scale(1.1);
  z-index: 20;
  box-shadow: 0 15px 35px -5px rgba(0, 0, 0, 0.5);
}

.text-shadow-lg {
  text-shadow: 3px 3px 0px #000;
}

@media (max-width: 768px) {
  .carousel-container {
    mask-image: none;
    -webkit-mask-image: none;
  }
  
  .value-card.is-selected {
    transform: scale(1.05);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
</style>
